import { Dispatch } from 'react';
import { Action, Reducer } from 'redux';
import InstanceClient from '../../services/InstanceClient';
import { RequestState } from '../../models/RequestState';
import StatusCode from '../../util/StatusCode';
import {
    CoreGoogleDeviceDashboard,
    GoogleDeviceStatusCount,
    GoogleDeviceCount
} from '../../models/CoreDeviceModels';

enum GoogleDeviceDashboardActionType {
    GOOGLE_DEVICE_DASHBOARD = 'GOOGLE_DEVICE_DASHBOARD',
    GOOGLE_DEVICE_STATUSES = 'GOOGLE_DEVICE_STATUSES',
    GOOGLE_DEVICE_AUTO_UPDATE = 'GOOGLE_DEVICE_AUTO_UPDATE',
    GOOGLE_DEVICE_SCHOOLS = 'GOOGLE_DEVICE_SCHOOLS',
    GOOGLE_DEVICE_MISSING_USER = 'GOOGLE_DEVICE_MISSING_USER',
    GOOGLE_DEVICE_OS_VERSIONS = 'GOOGLE_DEVICE_OS_VERSIONS',
}

export interface GoogleDeviceDashboardState {
    dashboard: RequestState<CoreGoogleDeviceDashboard>
    statuses: RequestState<GoogleDeviceStatusCount[]>
    autoUpdateExpiration: RequestState<GoogleDeviceCount[]>
    schools: RequestState<GoogleDeviceCount[]>
    missingUsers: RequestState<GoogleDeviceCount>
    osVersions: RequestState<GoogleDeviceCount[]>
}

interface GoogleDeviceDashboard_Get extends Action<GoogleDeviceDashboardActionType> {
    type: GoogleDeviceDashboardActionType.GOOGLE_DEVICE_DASHBOARD,
    dashboard: RequestState<CoreGoogleDeviceDashboard>
}

interface GoogleDeviceDashboard_GetStatuses extends Action<GoogleDeviceDashboardActionType> {
    type: GoogleDeviceDashboardActionType.GOOGLE_DEVICE_STATUSES,
    statuses: RequestState<GoogleDeviceStatusCount[]>
}

interface GoogleDeviceDashboard_GetAutoUpdateExpiration extends Action<GoogleDeviceDashboardActionType> {
    type: GoogleDeviceDashboardActionType.GOOGLE_DEVICE_AUTO_UPDATE,
    autoUpdateExpiration: RequestState<GoogleDeviceCount[]>
}

interface GoogleDeviceDashboard_GetDevicesPerSchool extends Action<GoogleDeviceDashboardActionType> {
    type: GoogleDeviceDashboardActionType.GOOGLE_DEVICE_SCHOOLS,
    schools: RequestState<GoogleDeviceCount[]>
}

interface GoogleDeviceDashboard_GetDevicesMissingUser extends Action<GoogleDeviceDashboardActionType> {
    type: GoogleDeviceDashboardActionType.GOOGLE_DEVICE_MISSING_USER,
    missingUsers: RequestState<GoogleDeviceCount>
}

interface GoogleDeviceDashboard_GetDevicesOsVersions extends Action<GoogleDeviceDashboardActionType> {
    type: GoogleDeviceDashboardActionType.GOOGLE_DEVICE_OS_VERSIONS,
    osVersions: RequestState<GoogleDeviceCount[]>
}

type GoogleDeviceDashboardAction = GoogleDeviceDashboard_Get
    | GoogleDeviceDashboard_GetStatuses
    | GoogleDeviceDashboard_GetAutoUpdateExpiration
    | GoogleDeviceDashboard_GetDevicesPerSchool
    | GoogleDeviceDashboard_GetDevicesMissingUser
    | GoogleDeviceDashboard_GetDevicesOsVersions;


export const GetGoogleDeviceDashboardInfo = () => (dispatch: Dispatch<GoogleDeviceDashboard_Get>) => {
    InstanceClient.get<CoreGoogleDeviceDashboard>('/api/core/googledevice/dashboard')
        .then(res => dispatch({
            type: GoogleDeviceDashboardActionType.GOOGLE_DEVICE_DASHBOARD,
            dashboard: { code: StatusCode.COMPLETE, data: res.data }
        }))
        .catch(error => dispatch({
            type: GoogleDeviceDashboardActionType.GOOGLE_DEVICE_DASHBOARD,
            dashboard: { code: StatusCode.ERROR, error }
        }));
}

export const GetGoogleDeviceDashboardStatus = () => (dispatch: Dispatch<GoogleDeviceDashboard_GetStatuses>) => {
    InstanceClient.get<GoogleDeviceStatusCount[]>('/api/core/googledevice/dashboard/statuses')
        .then(res => dispatch({
            type: GoogleDeviceDashboardActionType.GOOGLE_DEVICE_STATUSES,
            statuses: { code: StatusCode.COMPLETE, data: res.data }
        }))
        .catch(error => dispatch({
            type: GoogleDeviceDashboardActionType.GOOGLE_DEVICE_STATUSES,
            statuses: { code: StatusCode.ERROR, error }
        }));
}

export const GetGoogleDeviceDashboardAutoUpdate = () => (dispatch: Dispatch<GoogleDeviceDashboard_GetAutoUpdateExpiration>) => {
    InstanceClient.get<GoogleDeviceCount[]>('/api/core/googledevice/dashboard/autoUpdateExpiration')
        .then(res => dispatch({
            type: GoogleDeviceDashboardActionType.GOOGLE_DEVICE_AUTO_UPDATE,
            autoUpdateExpiration: { code: StatusCode.COMPLETE, data: res.data }
        }))
        .catch(error => dispatch({
            type: GoogleDeviceDashboardActionType.GOOGLE_DEVICE_AUTO_UPDATE,
            autoUpdateExpiration: { code: StatusCode.ERROR, error }
        }));
}

export const GetGoogleDeviceDashboardSchools = () => (dispatch: Dispatch<GoogleDeviceDashboard_GetDevicesPerSchool>) => {
    InstanceClient.get<GoogleDeviceCount[]>('/api/core/googledevice/dashboard/schools')
        .then(res => dispatch({
            type: GoogleDeviceDashboardActionType.GOOGLE_DEVICE_SCHOOLS,
            schools: { code: StatusCode.COMPLETE, data: res.data }
        }))
        .catch(error => dispatch({
            type: GoogleDeviceDashboardActionType.GOOGLE_DEVICE_SCHOOLS,
            schools: { code: StatusCode.ERROR, error }
        }));
}

export const GetGoogleDeviceDashboardMissingUsers = () => (dispatch: Dispatch<GoogleDeviceDashboard_GetDevicesMissingUser>) => {
    InstanceClient.get<GoogleDeviceCount>('/api/core/googledevice/dashboard/missingUser')
        .then(res => dispatch({
            type: GoogleDeviceDashboardActionType.GOOGLE_DEVICE_MISSING_USER,
            missingUsers: { code: StatusCode.COMPLETE, data: res.data }
        }))
        .catch(error => dispatch({
            type: GoogleDeviceDashboardActionType.GOOGLE_DEVICE_MISSING_USER,
            missingUsers: { code: StatusCode.ERROR, error }
        }));
}

export const GetGoogleDeviceDashboardOsVersions = () => (dispatch: Dispatch<GoogleDeviceDashboard_GetDevicesOsVersions>) => {
    InstanceClient.get<GoogleDeviceCount[]>('/api/core/googledevice/dashboard/osVersions')
        .then(res => dispatch({
            type: GoogleDeviceDashboardActionType.GOOGLE_DEVICE_OS_VERSIONS,
            osVersions: { code: StatusCode.COMPLETE, data: res.data }
        }))
        .catch(error => dispatch({
            type: GoogleDeviceDashboardActionType.GOOGLE_DEVICE_OS_VERSIONS,
            osVersions: { code: StatusCode.ERROR, error }
        }));
}

const initialState: GoogleDeviceDashboardState = {
    dashboard: { code: StatusCode.PENDING },
    statuses: { code: StatusCode.PENDING },
    autoUpdateExpiration: { code: StatusCode.PENDING },
    schools: { code: StatusCode.PENDING },
    missingUsers: { code: StatusCode.PENDING },
    osVersions: { code: StatusCode.PENDING },
};

export const reducer: Reducer<GoogleDeviceDashboardState, GoogleDeviceDashboardAction> = (state = initialState, action) => {
    state = state || initialState;

    switch (action.type) {
        case GoogleDeviceDashboardActionType.GOOGLE_DEVICE_DASHBOARD:
            return { ...state, dashboard: action.dashboard };
        case GoogleDeviceDashboardActionType.GOOGLE_DEVICE_STATUSES:
            return { ...state, statuses: action.statuses };
        case GoogleDeviceDashboardActionType.GOOGLE_DEVICE_AUTO_UPDATE:
            return { ...state, autoUpdateExpiration: action.autoUpdateExpiration };
        case GoogleDeviceDashboardActionType.GOOGLE_DEVICE_SCHOOLS:
            return { ...state, schools: action.schools };
        case GoogleDeviceDashboardActionType.GOOGLE_DEVICE_MISSING_USER:
            return { ...state, missingUsers: action.missingUsers };
        case GoogleDeviceDashboardActionType.GOOGLE_DEVICE_OS_VERSIONS:
            return { ...state, osVersions: action.osVersions };
        default:
            return state;
    }
}